import { useState, useEffect } from 'react';
import { Calendar, Link, Unlink, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useOutlookAuth } from '@/hooks/useOutlookAuth';
import { useOutlookCalendar } from '@/hooks/useOutlookCalendar';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

export function OutlookConnectButton() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { connectOutlook, disconnectOutlook, connecting } = useOutlookAuth();
  const { isConnected, loading } = useOutlookCalendar();
  const [tokenExpiry, setTokenExpiry] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (user && isConnected) {
      loadTokenExpiry();
    }
  }, [user, isConnected]);

  async function loadTokenExpiry() {
    const { data } = await supabase
      .from('user_settings')
      .select('personal_microsoft_token_expiry')
      .eq('user_id', user!.id)
      .single();
    
    if (data?.personal_microsoft_token_expiry) {
      setTokenExpiry(new Date(data.personal_microsoft_token_expiry));
    }
  }

  async function handleRefresh() {
    setRefreshing(true);
    try {
      const { error } = await supabase.functions.invoke('outlook-refresh-token');
      if (error) {
        throw error;
      }
      await loadTokenExpiry();
      toast({
        title: 'Token vernieuwd',
        description: 'Je Outlook token is succesvol vernieuwd'
      });
    } catch (error: any) {
      console.error('Refresh error:', error);
      toast({
        title: 'Vernieuwen mislukt',
        description: error.message || 'Kon token niet vernieuwen',
        variant: 'destructive'
      });
    } finally {
      setRefreshing(false);
    }
  }

  const isExpiringSoon = tokenExpiry && (tokenExpiry.getTime() - Date.now()) < 10 * 60 * 1000;
  const isExpired = tokenExpiry && tokenExpiry < new Date();

  if (loading) {
    return (
      <Button disabled>
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Laden...
      </Button>
    );
  }

  return (
    <div className="space-y-3">
      {isConnected ? (
        <>
          <Alert className={isExpired ? "bg-red-50 border-red-200" : isExpiringSoon ? "bg-amber-50 border-amber-200" : "bg-green-50 border-green-200"}>
            <Calendar className={`h-4 w-4 ${isExpired ? "text-red-600" : isExpiringSoon ? "text-amber-600" : "text-green-600"}`} />
            <AlertDescription className={isExpired ? "text-red-800" : isExpiringSoon ? "text-amber-800" : "text-green-800"}>
              {isExpired ? (
                "Je Outlook token is verlopen. Klik op vernieuwen."
              ) : isExpiringSoon ? (
                <>
                  Je Outlook agenda is gekoppeld. Token verloopt over {Math.round((tokenExpiry!.getTime() - Date.now()) / 60000)} minuten.
                </>
              ) : (
                <>
                  Je Outlook agenda is gekoppeld en wordt gesynchroniseerd.
                  {tokenExpiry && ` Token geldig tot ${tokenExpiry.toLocaleString('nl-NL')}`}
                </>
              )}
            </AlertDescription>
          </Alert>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
              {refreshing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Vernieuw Token
            </Button>
            <Button variant="outline" onClick={disconnectOutlook}>
              <Unlink className="h-4 w-4 mr-2" />
              Ontkoppel Outlook
            </Button>
          </div>
        </>
      ) : (
        <>
          <Alert className="bg-amber-50 border-amber-200">
            <Calendar className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              Koppel je Outlook agenda om afspraken te beheren vanuit het CRM
            </AlertDescription>
          </Alert>
          <Button onClick={connectOutlook} disabled={connecting}>
            {connecting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Link className="h-4 w-4 mr-2" />
            )}
            Koppel Outlook Agenda
          </Button>
        </>
      )}
    </div>
  );
}
